import { publishEmbeddedCanvas } from './embeddedCanvas'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export interface ThemeSnapshot {
  mode: ThemeMode
  resolved: ResolvedTheme
}

export const THEME_STORAGE_KEY = 'c2p-theme-mode'
export const THEME_CHANGE_EVENT = 'c2p:theme-change'

const themeModes = new Set<ThemeMode>(['light', 'dark', 'system'])
const themeColors: Record<ResolvedTheme, string> = {
  light: '#eff1f4',
  dark: '#0f1219',
}

function canUseDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function readStoredMode(): ThemeMode {
  if (!canUseDOM()) return 'system'
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY)
    return themeModes.has(value as ThemeMode) ? value as ThemeMode : 'system'
  } catch {
    return 'system'
  }
}

function writeStoredMode(mode: ThemeMode) {
  if (!canUseDOM()) return
  try {
    if (mode === 'system') window.localStorage.removeItem(THEME_STORAGE_KEY)
    else window.localStorage.setItem(THEME_STORAGE_KEY, mode)
  } catch {
    // Theme still applies for the current page even if storage is unavailable.
  }
}

function systemTheme(): ResolvedTheme {
  if (!canUseDOM() || !window.matchMedia) return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export function resolveTheme(mode: ThemeMode = readStoredMode()): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode
}

export function getThemeSnapshot(): ThemeSnapshot {
  const mode = readStoredMode()
  return { mode, resolved: resolveTheme(mode) }
}

export function applyThemeMode(mode: ThemeMode = readStoredMode()): ThemeSnapshot {
  const snapshot = { mode, resolved: resolveTheme(mode) }
  if (!canUseDOM()) return snapshot
  const root = document.documentElement
  root.dataset.theme = snapshot.resolved
  root.dataset.themeMode = snapshot.mode
  root.style.colorScheme = snapshot.resolved
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', themeColors[snapshot.resolved])
  publishEmbeddedCanvas()
  return snapshot
}

export function setThemeMode(mode: ThemeMode): ThemeSnapshot {
  writeStoredMode(mode)
  const snapshot = applyThemeMode(mode)
  if (canUseDOM()) {
    window.dispatchEvent(new CustomEvent<ThemeSnapshot>(THEME_CHANGE_EVENT, { detail: snapshot }))
  }
  return snapshot
}

export function toggleThemeMode(): ThemeSnapshot {
  const current = applyThemeMode()
  return setThemeMode(current.resolved === 'dark' ? 'light' : 'dark')
}

export function subscribeTheme(listener: (snapshot: ThemeSnapshot) => void) {
  if (!canUseDOM()) return () => undefined
  listener(applyThemeMode())
  const onThemeChange = (event: Event) => {
    listener(event instanceof CustomEvent ? event.detail as ThemeSnapshot : applyThemeMode())
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) listener(applyThemeMode())
  }
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  const onSystemChange = () => {
    const snapshot = applyThemeMode()
    if (snapshot.mode === 'system') listener(snapshot)
  }
  window.addEventListener(THEME_CHANGE_EVENT, onThemeChange)
  window.addEventListener('storage', onStorage)
  media?.addEventListener('change', onSystemChange)
  return () => {
    window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange)
    window.removeEventListener('storage', onStorage)
    media?.removeEventListener('change', onSystemChange)
  }
}

function updateThemeToggleButton(button: Element, snapshot: ThemeSnapshot) {
  const nextLabel = snapshot.resolved === 'dark' ? '切换为浅色模式' : '切换为深色模式'
  button.setAttribute('aria-label', nextLabel)
  button.setAttribute('title', nextLabel)
  button.setAttribute('data-theme-state', snapshot.resolved)
  button.querySelector<HTMLElement>('[data-theme-toggle-label]')?.replaceChildren(
    document.createTextNode(snapshot.resolved === 'dark' ? '浅色模式' : '深色模式'),
  )
}

export function syncThemeToggleButtons(root?: ParentNode, snapshot: ThemeSnapshot = applyThemeMode()) {
  if (!canUseDOM()) return
  const scope = root ?? document
  scope.querySelectorAll('[data-theme-toggle]').forEach((button) => updateThemeToggleButton(button, snapshot))
}

export function initThemeControls(root?: ParentNode & EventTarget) {
  if (!canUseDOM()) return () => undefined
  const scope = root ?? document
  const snapshot = applyThemeMode()
  syncThemeToggleButtons(scope, snapshot)

  const onClick = (event: Event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-theme-toggle]') : null
    if (!target) return
    syncThemeToggleButtons(scope, toggleThemeMode())
  }
  const onThemeChange = (event: Event) => {
    const snapshot = event instanceof CustomEvent ? event.detail as ThemeSnapshot : applyThemeMode()
    syncThemeToggleButtons(scope, snapshot)
  }
  const onStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return
    syncThemeToggleButtons(scope, applyThemeMode())
  }
  const media = window.matchMedia?.('(prefers-color-scheme: dark)')
  const onSystemChange = () => {
    const snapshot = applyThemeMode()
    if (snapshot.mode === 'system') syncThemeToggleButtons(scope, snapshot)
  }

  scope.addEventListener('click', onClick)
  window.addEventListener(THEME_CHANGE_EVENT, onThemeChange)
  window.addEventListener('storage', onStorage)
  media?.addEventListener('change', onSystemChange)

  return () => {
    scope.removeEventListener('click', onClick)
    window.removeEventListener(THEME_CHANGE_EVENT, onThemeChange)
    window.removeEventListener('storage', onStorage)
    media?.removeEventListener('change', onSystemChange)
  }
}

if (canUseDOM()) applyThemeMode()
